import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ConfirmationService, Dialog, MessageService, SelectItem } from 'primeng';
import { flatMap, fromPairs } from 'lodash';
import {
  ITaskSupplierScheduleStoreTask,
  TaskSupplierScheduleSource,
  TaskSupplierScheduleStore,
} from './task-supplier-schedule-store';
import {
  ConfirmAllocationRequest,
  JobSupplierScheduleContext,
  TaskScheduleDTO,
  UpdateTaskScheduleDTO,
} from './task-supplier-schedule';
import { JobsDeploymentService, JobsService, SettingsService, UserService } from '../../services';
import { ScrollPanel } from 'primeng/scrollpanel/scrollpanel';
import { TaskTypeHelper } from '../../utils/taskType.helper';
import { TaskCollectionTravelMode } from '../../enums/task-collection-travel-mode.enum';
import { IChecklistSubCategory } from '../../interfaces/checklist-category';
import { InputHelper } from '../../utils/input.helper';
import { IDeploySupplierContext } from '../../interfaces/deploy-supplier-context';
import { ScheduleUnavailabilitySummaryDTO } from '../../interfaces/supplier-availability/schedule-unavailability-summary.dto.interface';
import {
  AnalyticsJobComponent,
  JobAnalyticsService,
} from '../../services/product-analytics/job-analytics.service';
import { JobComponent } from '../job/job.component';
import {
  TravelSchedulerDTO,
  TravelSchedulerTask,
} from '../../interfaces/travel-scheduler/travel-scheduler-task';
import { TravelSchedulerService } from '../../services/travel-scheduler.service';
import { UpdateSupplierScheduleRequest } from '../../interfaces/travel-scheduler/update-supplier-schedule-request';
import { Message } from '../../classes/Message';
import { RefinerSurveyService } from '../../services/refiner-survey.service';
import { IVehicle, IVehicleAddress } from '../../interfaces/task-info';
import { PriceChangeReasonsService } from '../../services/price-change-reasons.service';
import { ValidateCarryoverAddressResponse } from '../../interfaces/carryover/validate-carryover-address';
import { CarryoverValidationUtils } from '../travel-scheduler/carryover-validation-utils';
import { OrganisationTypeEnum } from '../../enums/organisation-types';

export enum DialogStepPosition {
  /** Step will be shown before the task supplier schedule steps */
  Before = 0,
  /** Step will be shown after the task supplier schedule steps */
  After = 1,
}

export interface DialogStep {
  /** Dialog header text when this step is active */
  header: string;
  /** Icon class, displayed as part of header */
  headerIcon?: string;
  /** Class applied to dialog element when this step is active */
  dialogClass?: string;
  /** Whether the step is displayed before or after the supplier schedule steps */
  position: DialogStepPosition;
  /** Template ref which is rendered for this step */
  template?: TemplateRef<any>;
  /** Disables the next/confirm button, reactive */
  disabled?: boolean;
  /** Text for the next/confirm button when this step is active */
  nextBtnText?: string;
}

@Component({
  selector: 'supplier-schedule-dialog',
  templateUrl: './supplier-schedule-dialog.component.html',
  styleUrls: ['./supplier-schedule-dialog.component.scss'],
  providers: [ConfirmationService],
})
export class SupplierScheduleDialogComponent implements OnInit {
  public reloadedTabs: boolean = true;

  public mobileView: boolean = false;

  public submitAttempted: boolean = false;

  scheduleUnavailability: ScheduleUnavailabilitySummaryDTO;

  @ViewChild('dialog') dialog!: Dialog;

  @ViewChild('scrollPanel') scrollPanel!: ScrollPanel;

  @Input() taskId: number | null = null;

  @Input() supplierId: number | null = null;
  organisationTypeId: number;

  @Input() jobId: number;

  @Input() deploySupplierContext: IDeploySupplierContext;

  @Input() referrPage: string;

  tasks: TravelSchedulerTask[] = [];

  @Input() recommendedRouteId: number | null = null;

  priceInput: string;
  originalPrice: string;
  priceChangeReason: number | null = null;
  priceChangeReasonOtherText: string | null = null;

  priceChangeReasons: SelectItem[] = [];
  async initPriceChangeReasons() {
    this.priceChangeReasons = await this.priceChangeReasonService.getPriceChangeReasons();
  }

  get hasPriceChanged(): boolean {
    return this.originalPrice != this.priceInput;
  }

  private get otherReasonValue(): any {
    return this.priceChangeReasons.find((x) => x.label === 'Other')?.value;
  }

  get isOtherReasonSelected(): boolean {
    const other = this.otherReasonValue;
    return other != null && this.priceChangeReason === other;
  }

  get price(): number | null {
    return Number(this.priceInput) || null;
  }

  @Input() set price(value: number | null) {
    this.priceInput = value?.toFixed(2);
    this.originalPrice = this.priceInput;
  }

  @Input() visible: boolean = false;

  @Input() autoEdit: boolean = true;

  @Input() finalBtnText: string = 'Update';

  @Input() fetchChecklistCategory: boolean = false;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() priceChange: EventEmitter<number> = new EventEmitter<number>();

  @Output() confirm: EventEmitter<ConfirmAllocationRequest> =
    new EventEmitter<ConfirmAllocationRequest>();

  @Output() scheduleChanged: EventEmitter<UpdateTaskScheduleDTO[]> = new EventEmitter<
    UpdateTaskScheduleDTO[]
  >();

  @Output() show: EventEmitter<any> = new EventEmitter<any>();

  showSummary: boolean;

  public travelSchedulerDTO: TravelSchedulerDTO;

  public transportModes = [
    { value: TaskCollectionTravelMode.Driving, icon: 'la-car', label: 'Driving' },
    { value: TaskCollectionTravelMode.PublicTransport, icon: 'la-bus', label: 'Public Transport' },
    { value: TaskCollectionTravelMode.Walking, icon: 'la-walking', label: 'Walking' },
    { value: TaskCollectionTravelMode.Cycling, icon: 'la-biking', label: 'Cycling' },
  ];

  public get headerText(): string {
    if (this.taskScheduleSource == TaskSupplierScheduleSource.GrabJob) {
      return 'Grab Job';
    }
    if (this.taskScheduleSource == TaskSupplierScheduleSource.DriverAllocation) {
      return 'Allocate Driver';
    }
    if (this.taskScheduleSource == TaskSupplierScheduleSource.JobDeployment) {
      return 'Deploy Job';
    }
    return 'Edit Schedule';
  }

  public get allSteps(): DialogStep[] {
    return flatMap(this.editTaskList, (t, i) => {
      let store = this.taskSupplierScheduleStores[t.id];
      return [
        {
          header:
            this.tasks.length > 1
              ? `Edit Schedule - Task (${i + 1}/${this.tasks.length})`
              : 'Edit Schedule',
          dialogClass: '',
          icon: '',
          position: DialogStepPosition.Before,
          disabled: store && !store.noneSameDayDeliveryReasonValid,
        },
      ];
    });
  }

  public get activeStep(): DialogStep {
    let activeStep = this.allSteps[this.activeIndex];

    if (this.activeIndex >= 0) {
      activeStep.disabled =
        this.tasks[this.activeIndex].supplierPickupWindow == null ||
        this.tasks[this.activeIndex].supplierDropoffWindow == null ||
        !this.isCarryoverAddressAccepted(this.tasks[this.activeIndex]);
    }

    return activeStep;
  }

  protected getCarryoverValidationResult(): ValidateCarryoverAddressResponse {
    return this.tasks?.[this.activeIndex]?.carryoverValidation ?? null;
  }

  protected handleCarryoverValidationResult(result: ValidateCarryoverAddressResponse) {
    const task = this.tasks?.[this.activeIndex];

    if (!task) {
      return;
    }

    task.carryoverAddressChanged = true;
    task.carryoverValidation = result;
  }

  /**
   * VME users are never held up by the carryover mileage check; they are warned about an unapproved
   * carryover instead. Suppliers have to have a validated carryover address before moving on.
   */
  private isCarryoverAddressAccepted(task: TravelSchedulerTask): boolean {
    if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
      return true;
    }

    return CarryoverValidationUtils.isCarryoverValidated(task);
  }

  /** Explains why Continue is unavailable. Shown on the button itself, so it also works while disabled. */
  public get continueBtnTooltip(): string {
    const task = this.tasks?.[this.activeIndex];

    if (task && !this.isCarryoverAddressAccepted(task)) {
      return 'A suitable carryover parking address is needed before continuing.';
    }

    return null;
  }

  public taskSupplierScheduleStores: Record<string, TaskSupplierScheduleStore> = {};

  public activeIndex: number = 0;

  @Input() taskScheduleSource: TaskSupplierScheduleSource;

  @Input()
  set finalBtnDisabled(value: boolean) {
    this._finalBtnDisabled = value;
  }
  get finalBtnDisabled(): boolean {
    // Preserve any parent-driven disabling AND apply price-change reason requirements
    return this._finalBtnDisabled || !this.priceChangeReasonValid;
  }
  private _finalBtnDisabled: boolean = false;

  private get priceChangeReasonValid(): boolean {
    // Only required when the price has changed
    if (!this.hasPriceChanged) return true;

    // Dropdown must be selected
    if (this.priceChangeReason == null) return false;

    // If "Other" selected, free text must be provided
    if (this.isOtherReasonSelected) {
      const text = (this.priceChangeReasonOtherText ?? '').trim();
      return text.length > 0;
    }

    return true;
  }

  public TaskSupplierScheduleSource = TaskSupplierScheduleSource;

  public canEdit: boolean = true;
  checklist: IChecklistSubCategory;

  public get showTotalEarnings(): boolean {
    return this.taskScheduleSource == TaskSupplierScheduleSource.GrabJob;
  }

  public get questionText(): string {
    if (
      this.taskScheduleSource == TaskSupplierScheduleSource.JobDeployment ||
      this.taskScheduleSource == TaskSupplierScheduleSource.DriverAllocation ||
      this.taskScheduleSource == TaskSupplierScheduleSource.EngineiusEdit
    ) {
      return 'When will the supplier pick-up and drop-off?';
    }

    return 'When would you like to pick-up and drop-off?';
  }

  public get confirmText(): string {
    if (this.taskScheduleSource == TaskSupplierScheduleSource.JobDeployment) {
      return 'Confirm schedule and deploy job';
    }

    if (this.taskScheduleSource == TaskSupplierScheduleSource.GrabJob) {
      return 'Confirm schedule and grab job';
    }

    if (this.taskScheduleSource == TaskSupplierScheduleSource.DriverAllocation) {
      return 'Confirm schedule and allocate supplier';
    }

    return 'Confirm schedule';
  }

  public get editTaskList(): TravelSchedulerTask[] {
    return this.canEdit ? this.tasks : [];
  }

  public get travelModeValid(): boolean {
    return Object.values(this.taskSupplierScheduleStores).every((x) => x.pickUp.travelMode != null);
  }

  public get finalBtnTooltip(): string {
    return this.travelModeValid ? '' : 'Please select a travel mode for all tasks';
  }

  constructor(
    private travelSchedulerService: TravelSchedulerService,
    private taskTypeHelper: TaskTypeHelper,
    public inputHelper: InputHelper,
    private jobDeploymentService: JobsDeploymentService,
    private userService: UserService,
    private confirmationService: ConfirmationService,
    private jobAnalyticsService: JobAnalyticsService,
    private priceChangeReasonService: PriceChangeReasonsService,
  ) {
    const currentOrgPromise = this.userService.getCurrentUserOrg();

    currentOrgPromise.then((org) => {
      this.organisationTypeId = org.organisationTypeId;
    });
  }

  async ngOnInit() {
    await this.reloadTabs();
    await this.initPriceChangeReasons();
  }

  public get currentStep(): DialogStep {
    return (
      this.allSteps[this.activeIndex] || {
        dialogClass: '',
        position: DialogStepPosition.After,
        header: '',
      }
    );
  }

  public get nextEnabled() {
    return this.activeIndex < this.allSteps.length && !this.showSummary;
  }

  public get backEnabled() {
    return this.canEdit && (this.activeIndex > 0 || this.showSummary);
  }

  reloadTabs(): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => (this.reloadedTabs = false));
      setTimeout(() => {
        this.reloadedTabs = true;
        resolve();
      });
    });
  }

  close() {
    this.visibleChange.emit(false);
  }

  next() {
    if (this.nextEnabled) {
      this.jobAnalyticsService.sendGrabJobContinueEvent(this.getAnalyticsJobComponent());
      if (this.activeIndex == this.allSteps.length - 1) {
        this.showSummary = true;
        this.jobAnalyticsService.sendGrabJobSummaryEvent(this.getAnalyticsJobComponent());
      } else {
        this.activeIndex = this.activeIndex + 1;
      }
    }
  }

  back() {
    if (this.backEnabled) {
      if (this.showSummary) {
        this.showSummary = false;
      } else {
        this.activeIndex = this.activeIndex - 1;
      }
      this.jobAnalyticsService.sendGrabJobEvent(this.getAnalyticsJobComponent());
    }
  }

  onVisibleChange(isVisible: boolean) {
    if (isVisible) {
      this.visibleChange.emit(true);
    } else {
      this.onCancel();
      this.visibleChange.emit(false);
    }
  }

  onCancel() {
    this.jobAnalyticsService.sendGrabJobExitEvent(this.getAnalyticsJobComponent());
    this.visibleChange.emit(false);
  }

  async onShow() {
    let width =
      window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (width <= 576) {
      this.dialog.maximize();
    }

    this.travelSchedulerDTO = await this.travelSchedulerService.get(this.jobId, this.supplierId);

    this.tasks = this.travelSchedulerDTO.tasks;

    this.submitAttempted = false;

    this.mobileView = width < 850;

    this.canEdit = this.autoEdit;

    this.activeIndex = 0;

    this.showSummary = !this.canEdit;

    // this.activeIndex = 0;

    // Strange bug with p-tabView was causing two tabs to be shown
    // at once. Force re-render of tabs
    await this.reloadTabs();

    this.jobAnalyticsService.sendGrabJobEvent(this.getAnalyticsJobComponent());

    this.show.emit();
  }

  private getAnalyticsJobComponent(): AnalyticsJobComponent {
    return {
      jobNumber: this.travelSchedulerDTO.jobNumber,
      journeyLeg: this.tasks.length,
      referrerPage: this.referrPage,
      tasks: this.tasks.map((task) => this.mapToITaskSupplierScheduleStoreTask(task)),
      totalEarnings: this.price,
    } as AnalyticsJobComponent;
  }

  private mapToITaskSupplierScheduleStoreTask(
    task: TravelSchedulerTask,
  ): ITaskSupplierScheduleStoreTask {
    return {
      id: task.id,
      jobId: this.jobId,

      pickupEarliestDateTime: new Date(task.customerPickupEarliest).toISOString(),
      pickupLatestDateTime: new Date(task.customerPickupLatest).toISOString(),

      dropoffEarliestDateTime: new Date(task.customerDropoffEarliest).toISOString(),
      dropoffLatestDateTime: new Date(task.customerDropoffLatest).toISOString(),

      taskSupplierSchedule: this.mapToTaskScheduleDTO(task),
      estimateTime: `${task.taskDurationMinutes}`,

      vehicle: {
        name: task.vehicle,
        registration: task.vehicleReg,
        isEv: task.isEv,
      } as unknown as IVehicle,

      pickupLocation: {
        addressLine1: task.pickupAddress,
      } as IVehicleAddress,

      dropoffLocation: {
        addressLine1: task.dropoffAddress,
      } as IVehicleAddress,

      estimateTimeHHmm: this.minutesToHHmm(task.taskDurationMinutes),
      distance: task.taskMileage,

      taskTypeName: null,

      carryOverAuthorised: task.isCarryoverPossible,

      checklistSubCategories: task.checklistSubCategories,
    };
  }

  private mapToTaskScheduleDTO(task: TravelSchedulerTask): TaskScheduleDTO {
    return {
      taskId: task.id,
      specifiedPickUpTime: new Date(
        task.supplierPickupWindow ?? task.customerPickupEarliest,
      ).toISOString(),

      specifiedDropOffTime: new Date(
        task.supplierDropoffWindow ?? task.customerDropoffLatest,
      ).toISOString(),

      taskCollectionTravelMode: task.supplierTransportModeToPickup,

      noneSameDayDeliveryReasonId: 0,

      defaultTaskCollectionTravelMode: task.supplierTransportModeToPickup ?? undefined,
      carryoverAddress: task.carryoverAddress,
    };
  }

  /**
   * Helper to convert minutes into HH:mm format
   */
  private minutesToHHmm(minutes: number): string {
    const hrs = Math.floor(minutes / 60)
      .toString()
      .padStart(2, '0');
    const mins = (minutes % 60).toString().padStart(2, '0');
    return `${hrs}:${mins}`;
  }

  onFinalStepConfirm() {
    if (this.travelSchedulerDTO.grabDisabled) {
      this.confirmationService.confirm({
        message: 'The job has grabbing disabled. Please confirm you want to deploy this job.',
        header: 'Deploy Confirmation',
        accept: () => this.finalStepConfirm(),
        reject: () => {},
      });
    } else {
      this.finalStepConfirm();
    }
  }

  finalStepConfirm() {
    this.submitAttempted = true;
    this.priceChange.emit(Number(this.priceInput));
    this.confirm.emit({
      updateTaskScheduleDTOs: this.tasks.map((t) => this.mapToUpdateSupplierSchedule(t)),
      priceChangeReason:
        this.priceChangeReason != null
          ? {
              id: this.priceChangeReason,
              otherReason: this.priceChangeReasonOtherText,
            }
          : null,
    });
  }

  mapToUpdateSupplierSchedule(task: TravelSchedulerTask): UpdateTaskScheduleDTO {
    return new UpdateTaskScheduleDTO({
      noneSameDayDeliveryReasonId: null,
      taskId: task.id,
      specifiedPickUpTime: task.supplierPickupWindow
        ? new Date(task.supplierPickupWindow).toISOString()
        : null,
      specifiedDropOffTime: task.supplierDropoffWindow
        ? new Date(task.supplierDropoffWindow).toISOString()
        : null,
      taskCollectionTravelMode: task.supplierTransportModeToPickup,
      carryoverAddress: task.carryoverAddress,
    });
  }

  taskTypeName(taskTypeId: any) {
    return this.taskTypeHelper.getTaskTypeName(taskTypeId);
  }

  differentDates(date1: Date, date2: Date) {
    if (date1 == null || date2 == null) {
      return false;
    }

    return new Date(date1).toDateString() != new Date(date2).toDateString();
  }

  getScheduleStore(taskId: number) {
    return this.taskSupplierScheduleStores[taskId];
  }

  addHour(scheduledTime: Date | undefined) {
    if (scheduledTime) {
      let date = new Date(scheduledTime);
      date.setHours(date.getHours() + 1);
      return date;
    }
  }

  subtractHour(scheduledTime: Date | undefined) {
    if (scheduledTime) {
      let date = new Date(scheduledTime);
      date.setHours(date.getHours() - 1);
      return date;
    }
  }

  getUnavailability(taskId: number) {
    return this.scheduleUnavailability?.scheduleUnavailabilityList?.filter(
      (x) => x.taskId == taskId,
    );
  }

  protected handleScheduleSelected(task: TravelSchedulerTask) {
    this.tasks[this.activeIndex] = task;

    let schedules = this.tasks.map((t) => this.mapToUpdateSupplierSchedule(t));
    this.jobDeploymentService.checkScheduleAvailability(this.supplierId, schedules).then((res) => {
      this.scheduleUnavailability = res;
    });
  }

  getPreviousTaskDropoffWindow() {
    if (this.activeIndex <= 0) {
      return null;
    }

    const supplierDropoffWindow = this.tasks[this.activeIndex - 1].supplierDropoffWindow;

    return typeof supplierDropoffWindow == 'string'
      ? new Date(supplierDropoffWindow)
      : supplierDropoffWindow;
  }
  @Output() editScheduleClicked = new EventEmitter<number>();
  handleEditSchedule($event: number) {
    this.editScheduleClicked.emit($event);
  }
}
